import {
    getSubscriptionItem,
    listSubscriptionItems
} from './queries';
import {
    createSubscriptionItem,
    deleteSubscriptionItem,
    updateSubscriptionItem,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {SubscriptionItem} from "../types.ts";
import {generateClient} from "aws-amplify/api";


export type ListSubscriptionItems = {
    listSubscriptionItems: { items: SubscriptionItem[], nextToken?: string }
}

export type GetSubscriptionItem = {
    getSubscriptionItem: SubscriptionItem
}

export type CreateSubscriptionItem = {
    createSubscriptionItem: SubscriptionItem
}

export type UpdateSubscriptionItem = {
    updateSubscriptionItem: SubscriptionItem
}

export type DeleteSubscriptionItem = {
    deleteSubscriptionItem: string
}


const client = generateClient();

/**
 * FNP-918 — ⚠️ THIS LIST WAS NEVER FILTERED.
 *
 * The query declares `$filter: SubscriptionItemsFilterInput` and passes it to
 * `listSubscriptionItems(filter: $filter, ...)`, but the variables sent were
 * `{customerId, limit, nextToken}` — there is no `$customerId` in the document,
 * so it was ignored and `$filter` arrived UNDEFINED. The API then returned
 * every subscription item it would list for the caller, not the ones belonging
 * to the subscription being viewed.
 *
 * The schema is explicit about the right shape:
 *
 *   input SubscriptionItemsFilterInput { subscription: String! }
 *
 * so the scope is the SUBSCRIPTION id, not a customer id. The parameter was
 * named `customerId` throughout this module while being handed a subscription
 * id by its only caller (subscriptions/pages/EditPage renders this list for
 * `:id` on the subscription route). Renamed to `subscriptionId` so the name
 * matches what it holds.
 */
export const fetchRecords = async (
    subscriptionId: string,
    limit?: number,
    nextToken?: string,
) => {

    const response = await client.graphql<GraphQLQuery<ListSubscriptionItems>>({
                                                                                   query: listSubscriptionItems,
                                                                                   variables: {
                                                                                       filter: {subscription: subscriptionId},
                                                                                       limit,
                                                                                       nextToken,
                                                                                   },
                                                                               });
    return response.data?.listSubscriptionItems;
};

/** `getSubscriptionItem(id: ID!)` — the id alone identifies the record. */
export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetSubscriptionItem>>({
                                                                                 query: getSubscriptionItem,
                                                                                 variables: {id},
                                                                             });
    return response.data?.getSubscriptionItem;
};

/**
 * FNP-918: payloads are WHITELISTED to the API's Input type (§6).
 *
 * ⚠️ `update` previously ran `delete record.created; delete record.updated;`
 * on the object it was HANDED — the caller's live Formik values / React Query
 * cache entry — and then spread the rest. Deleting is the wrong direction: it
 * removes the keys you remembered to name and sends everything you did not.
 *
 * Undefined entries are dropped so an untouched optional field is omitted
 * rather than sent as an explicit null.
 */
const pick = <T extends object>(source: T, keys: (keyof T)[]): Record<string, unknown> =>
    keys.reduce<Record<string, unknown>>((acc, key) => {
        const value = source[key];
        if (typeof value !== 'undefined') {
            acc[key as string] = value;
        }
        return acc;
    }, {});

/** Exactly the fields CreateSubscriptionItemInput declares. */
const CREATE_FIELDS: (keyof Omit<SubscriptionItem, 'id'>)[] = [
    'price',
    'quantity',
    'subscription',
];

/**
 * Exactly the fields UpdateSubscriptionItemInput declares.
 *
 * ⚠️ Narrower than create: the API accepts NO `subscription` on update — an
 * item cannot be moved between subscriptions.
 */
const UPDATE_FIELDS: (keyof SubscriptionItem)[] = [
    'id',
    'price',
    'quantity',
];

export const add = async (payload: Omit<SubscriptionItem, 'id'>) => {

    const response = await client.graphql<GraphQLQuery<CreateSubscriptionItem>>({
                                                                                    query: createSubscriptionItem,
                                                                                    variables: {input: pick(payload, CREATE_FIELDS)},
                                                                                });

    return response.data?.createSubscriptionItem;
};

export const update = async (record: SubscriptionItem) => {

    const response = await client.graphql<GraphQLQuery<UpdateSubscriptionItem>>({
                                                                                    query: (updateSubscriptionItem),
                                                                                    variables: {input: pick(record, UPDATE_FIELDS)},
                                                                                });
    return response.data?.updateSubscriptionItem;
};

/**
 * ⚠️ FNP-918: this sent `{customerId, id}`. `DeleteSubscriptionItemInput`
 * declares `id: ID!` and nothing else, so the extra field was an unknown-field
 * rejection on an Input type the API validates strictly.
 */
export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeleteSubscriptionItem>>({
                                                                                    query: (deleteSubscriptionItem),
                                                                                    variables: {input: {id}},
                                                                                });
    return response.data?.deleteSubscriptionItem;
};
